<script setup lang="ts">
import { ref } from 'vue';
import VueEasyLightbox from 'vue-easy-lightbox';
import { Picture } from '~/types';
import Image from '~/components/ui/Image.vue';
import Container from '~/components/Container.vue';

const visibleRef = ref(false);

const indexRef = ref<number | undefined>(0);

const onHide = () => (visibleRef.value = false);

function onShow(index: number): void {
  visibleRef.value = true;
  indexRef.value = index;
}

interface Props {
  anchor?: string;
  title?: string;
  description?: string;
  images: Picture[];
}

defineProps<Props>();
</script>

<template>
  <section v-if="images && images.length > 0" :id="anchor" class="gallery-grid">
    <Container v-if="title || description" class="gallery-grid__heading">
      <h2 v-if="title" class="gallery-grid__title">
        {{ title }}
      </h2>
      <p v-if="description" class="gallery-grid__description">
        {{ description }}
      </p>
    </Container>
    <Container is-fluid>
      <teleport to="body">
        <VueEasyLightbox :visible="visibleRef" :imgs="images" :index="indexRef" @hide="onHide" />
      </teleport>
      <ul class="gallery-grid__list">
        <li v-for="(image, index) in images" :key="`gallery-grid-image-${index}`" class="gallery-grid__item">
          <a href="#" role="button" class="gallery-grid__image-wrapper" @click.prevent="onShow(index)">
            <Image :image="image" class="gallery-grid__image" />
          </a>
        </li>
      </ul>
    </Container>
  </section>
</template>

<style lang="scss" scoped>
.gallery-grid {
  @apply my-10 overflow-hidden;
  @apply md:my-16;
  @apply xl:my-24;

  &__heading {
    @apply flex flex-col items-center text-center mb-6 gap-6;
    @apply xl:mb-12 xl:gap-10;
  }

  &__title {
    @apply font-semibold text-[24px] leading-[1.1];
    @apply xl:text-[38px];
  }

  &__description {
    @apply font-medium leading-[1.3] text-[16px] text-base-color/80;
    @apply xl:text-[20px] xl:max-w-[60%] xl:mx-auto;
  }

  &__list {
    @apply w-full flex flex-wrap -mt-4;
    @apply md:w-[calc(100%+1rem)] md:-ml-2;
  }

  &__item {
    @apply w-full pt-4;
    @apply md:w-1/2 md:px-2;
    @apply xl:w-1/3;
  }

  &__image-wrapper {
    @apply relative block overflow-hidden rounded-10 aspect-[16/9];
  }

  &__image {
    @apply absolute w-full h-full inset-0 object-cover object-center duration-300;
  }

  &__image-wrapper:hover &__image {
    @apply scale-105;
  }
}
</style>
